import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {String} filename - Filename without extension
 * @param {String} sheetName - Sheet name
 * @param {Object} filters - Applied filters (optional)
 */
export const exportToExcel = async (data, filename = 'export', sheetName = 'Data', filters = null) => {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Add headers
    worksheet.addRow(headers);
    
    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF0077B5' } // LinkedIn Blue
    };
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    // Add data rows
    data.forEach(item => {
      const row = headers.map(header => item[header] || '');
      worksheet.addRow(row);
    });

    // Auto-size columns
    worksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, cell => {
        const cellLength = cell.value ? cell.value.toString().length : 0;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });
      column.width = Math.min(Math.max(maxLength + 2, 10), 50);
    });

    // Freeze top row
    worksheet.views = [
      { state: 'frozen', xSplit: 0, ySplit: 1 }
    ];

    // Add filters info as comment if provided
    if (filters) {
      worksheet.getCell('A1').note = `Filters applied: ${JSON.stringify(filters)}`;
    }

    // Add footer with generation timestamp
    const footerRow = worksheet.addRow([`Generated: ${new Date().toLocaleString()}`]);
    footerRow.font = { italic: true, size: 10 };

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${Date.now()}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);

    return true;
  } catch (error) {
    console.error('Excel export error:', error);
    throw error;
  }
};

/**
 * Export chart and data to PDF
 * @param {Object} options - Export options
 * {
 *   title: string,
 *   data: array,
 *   chartElementId: string (element to capture as image),
 *   summary: object (key-value pairs for summary),
 *   filename: string,
 *   filters: object
 * }
 */
export const exportToPDF = async (options) => {
  const {
    title = 'Report',
    data = [],
    chartElementId = null,
    summary = {},
    filename = 'report',
    filters = null
  } = options;

  try {
    const doc = new jsPDF();
    let yPosition = 20;

    // Add title
    doc.setFontSize(20);
    doc.setTextColor(0, 119, 181); // LinkedIn Blue
    doc.text(title, 105, yPosition, { align: 'center' });
    yPosition += 15;

    // Add generation date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 105, yPosition, { align: 'center' });
    yPosition += 10;

    // Add filters if provided
    if (filters && Object.keys(filters).length > 0) {
      doc.setFontSize(9);
      doc.text('Applied Filters:', 20, yPosition);
      yPosition += 5;
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          doc.text(`  ${key}: ${value}`, 20, yPosition);
          yPosition += 5;
        }
      });
      yPosition += 5;
    }

    // Add summary section
    if (summary && Object.keys(summary).length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Summary', 20, yPosition);
      yPosition += 8;

      const summaryData = Object.entries(summary).map(([key, value]) => [key, value.toLocaleString ? value.toLocaleString() : value]);
      
      doc.autoTable({
        startY: yPosition,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [0, 119, 181], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: 20, right: 20 }
      });

      yPosition = doc.lastAutoTable.finalY + 15;
    }

    // Add chart image if element provided
    if (chartElementId) {
      const chartElement = document.getElementById(chartElementId);
      if (chartElement) {
        try {
          const canvas = await html2canvas(chartElement, {
            scale: 2,
            backgroundColor: '#ffffff'
          });
          
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = 170;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          // Add new page if needed
          if (yPosition + imgHeight > 270) {
            doc.addPage();
            yPosition = 20;
          }

          doc.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
          yPosition += imgHeight + 15;
        } catch (error) {
          console.error('Chart capture error:', error);
        }
      }
    }

    // Add data table (top 100 rows for PDF)
    if (data && data.length > 0) {
      // Add new page if needed
      if (yPosition > 200) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Data Preview (Top 100 Records)', 20, yPosition);
      yPosition += 8;

      const headers = Object.keys(data[0]);
      const tableData = data.slice(0, 100).map(item => 
        headers.map(header => {
          const value = item[header];
          if (value === null || value === undefined) return '';
          return value.toString().length > 50 ? value.toString().substring(0, 47) + '...' : value.toString();
        })
      );

      doc.autoTable({
        startY: yPosition,
        head: [headers],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [0, 119, 181], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { left: 10, right: 10 },
        columnStyles: headers.reduce((acc, header, index) => {
          acc[index] = { cellWidth: 'auto' };
          return acc;
        }, {})
      });
    }

    // Add footer to all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, 105, 290, { align: 'center' });
    }

    // Save PDF
    doc.save(`${filename}_${Date.now()}.pdf`);

    return true;
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

/**
 * Format date for display
 * @param {String|Date} date - Date to format
 * @returns {String} Formatted date
 */
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Format number with commas
 * @param {Number} num - Number to format
 * @returns {String} Formatted number
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '';
  return num.toLocaleString();
};

/**
 * Calculate percentage
 * @param {Number} part - Part value
 * @param {Number} total - Total value
 * @returns {String} Percentage with 1 decimal
 */
export const calculatePercentage = (part, total) => {
  if (!total || total === 0) return '0.0%';
  return ((part / total) * 100).toFixed(1) + '%';
};


